import { useMemo, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturingClient";
import {
  matchSpokenWalkMaterial,
  parseSpokenWalk,
  type SpokenWalkKind,
} from "@/lib/manufacturing/spokenWalkDictation";
import { ghostBtnClass, mfgPrimaryBtnClass } from "./manufacturingSurfaceClasses";
import { ManufacturingDictatePad } from "./ManufacturingDictatePad";

const COPY: Record<SpokenWalkKind, { title: string; hint: string; placeholder: string }> = {
  packed: {
    title: "Dictate this count",
    hint: "Say “packed four, bays two, item two by six pressure treated.” Match fills the form. Confirm still posts.",
    placeholder: "packed 4, bays 2, item 2x6 pressure treated…",
  },
  waste: {
    title: "Dictate this pile",
    hint: "Say “I see four two by six, tell them to use two.” Match fills the form. Confirm still posts.",
    placeholder: "I see 4 2x6, tell them to use 2…",
  },
};

export function WalkDictationPad({
  kind,
  materials,
  disabled,
  onApply,
}: {
  kind: SpokenWalkKind;
  materials: MfgMaterial[];
  disabled?: boolean;
  onApply: (row: { material: MfgMaterial; primaryQty: number; secondaryQty: number | null }) => void;
}) {
  const copy = COPY[kind];
  const [note, setNote] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    material: MfgMaterial;
    primaryQty: number;
    secondaryQty: number | null;
    status: string;
  } | null>(null);
  const [padReset, setPadReset] = useState(0);
  const catalog = useMemo(
    () => materials.map((row) => ({ id: row.id, name: row.name, code: row.code })),
    [materials],
  );

  function handleExtract(transcript: string) {
    const draft = parseSpokenWalk(kind, transcript);
    if (!draft) {
      setPending(null);
      setNote(
        kind === "packed"
          ? "Couldn’t find a packed quantity and a material. Try “packed 4, bays 2, item 2x6 pressure treated.”"
          : "Couldn’t find what you see and a material. Try “I see 4 2x6, tell them to use 2.”",
      );
      return;
    }
    const match = matchSpokenWalkMaterial(draft, catalog);
    const material = materials.find((row) => row.id === match.candidate?.id) ?? null;
    if (!material || match.status === "unmatched") {
      setPending(null);
      setNote(`Heard “${draft.materialName}” and nothing in this plant catalog matched.`);
      return;
    }
    setPending({
      material,
      primaryQty: draft.primaryQty,
      secondaryQty: draft.secondaryQty,
      status: match.status,
    });
    setNote(
      draft.secondaryQty == null && kind === "waste"
        ? "Use was not heard. Leave it blank and the whole pile counts as scrap, or type how many to use."
        : null,
    );
  }

  function fill() {
    if (!pending) return;
    onApply(pending);
    setPending(null);
    setNote("Filled the form. Confirm when the numbers look right.");
    setPadReset((current) => current + 1);
  }

  return (
    <div className="space-y-2 rounded-lg border border-violet-200 bg-violet-50/40 p-3 dark:border-violet-900 dark:bg-violet-950/20">
      <p className="text-xs font-semibold text-gray-800 dark:text-zinc-100">{copy.title}</p>
      <p className="text-[11px] text-gray-500">{copy.hint}</p>
      <ManufacturingDictatePad
        disabled={disabled}
        placeholder={copy.placeholder}
        extractLabel="Match material"
        resetKey={padReset}
        onComplete={handleExtract}
      />
      {note ? <p className="text-[11px] text-amber-800 dark:text-amber-200">{note}</p> : null}
      {pending ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-800 dark:text-zinc-100">
          <p>
            {pending.material.code} · {pending.material.name} ({pending.status}).{" "}
            {kind === "packed" ? "Packed" : "Seen"} {pending.primaryQty}
            {pending.secondaryQty == null
              ? ""
              : `, ${kind === "packed" ? "bays" : "use"} ${pending.secondaryQty}`}
            .
          </p>
          <div className="flex gap-2">
            <button type="button" className={ghostBtnClass} disabled={disabled} onClick={() => setPending(null)}>
              Discard
            </button>
            <button type="button" className={mfgPrimaryBtnClass} disabled={disabled} onClick={fill}>
              Fill form
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
